import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Circle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchInitiatives } from '../../utils/api';
import { InitiativesDB } from '../../utils/initiativesData';
import { Button } from '../ui';

function getHealthIcon(health) {
  const color = getHealthColor(health);
  const size = 14;
  return <Circle size={size} color={color} fill={color} strokeWidth={2} style={{ verticalAlign: 'middle' }} />;
}

function getHealthColor(health) {
  switch (health) {
    case 'green': return '#10B981';
    case 'yellow': return '#F59E0B';
    case 'red': return '#EF4444';
    default: return '#6B7280';
  }
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function InitiativeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [initiative, setInitiative] = useState(null);

  useEffect(() => {
    if (!id) return;
    fetchInitiatives('all', getToken).then(list => {
      const data = (Array.isArray(list) ? list : []).find(i => i.id === id);
      setInitiative(data || null);
    }).catch(() => setInitiative(null));
  }, [id, getToken]);

  if (!initiative) {
    return (
      <div className="dashboard-container">
        <p className="text-text-dark-secondary">Loading…</p>
      </div>
    );
  }

  const healthIcon = getHealthIcon(initiative.overallHealth);
  const healthColor = getHealthColor(initiative.overallHealth);

  const handleEdit = () => navigate(`/strategic-initiatives/${id}/edit`);
  const handleStatusUpdate = () => navigate(`/strategic-initiatives/${id}/status-update`);
  const handleBack = () => navigate('/strategic-initiatives');
  const handleDelete = () => {
    if (window.confirm('Delete this initiative? This cannot be undone.')) {
      InitiativesDB.deleteInitiative(id);
      navigate('/strategic-initiatives');
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <Button type="button" variant="default" size="sm" onClick={handleBack} style={{ marginBottom: 12 }}>← Back to Initiatives</Button>
          <h1 className="dashboard-title" style={{ margin: '0 0 4px 0' }}>{initiative.name}</h1>
          <p className="dashboard-subtitle" style={{ margin: 0 }}>{initiative.description || 'No description'}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button type="button" variant="default" size="sm" onClick={handleEdit}>Edit</Button>
          <Button type="button" variant="primary" size="sm" onClick={handleStatusUpdate}>Submit Update</Button>
          <Button type="button" variant="danger" size="sm" onClick={handleDelete}>Delete</Button>
        </div>
      </div>

      <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ marginBottom: 24 }}>
        <div className="p-5">
          <h3 className="text-[15px] font-semibold text-text-dark" style={{ marginTop: 0 }}>Overview</h3>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 20 }}>
            <div><strong>Status:</strong> {healthIcon} {initiative.status}</div>
            <div><strong>Category:</strong> {initiative.category}</div>
            <div><strong>Program Manager:</strong> {initiative.programManagerName || 'Unassigned'}</div>
            <div><strong>Start:</strong> {formatDate(initiative.startDate)}</div>
            <div><strong>Due:</strong> {formatDate(initiative.dueDate)}</div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <strong>Progress</strong>
            <div style={{ height: 10, background: '#F3F4F6', borderRadius: 5, overflow: 'hidden', marginTop: 8 }}>
              <div style={{ width: `${initiative.percentComplete || 0}%`, height: '100%', background: healthColor, transition: 'width 0.3s' }} />
            </div>
            <div className="text-text-dark-secondary" style={{ fontSize: 14, marginTop: 4 }}>{initiative.percentComplete || 0}% Complete</div>
          </div>
          {initiative.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {initiative.tags.map(tag => (
                <span key={tag.id} style={{ padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 500, background: `${tag.color}20`, color: tag.color }}>{tag.label}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {initiative.kpis?.length > 0 && (
        <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ marginBottom: 24 }}>
          <div className="p-5">
            <h3 className="text-[15px] font-semibold text-text-dark" style={{ marginTop: 0 }}>KPIs</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
              {initiative.kpis.map(kpi => (
                <div key={kpi.id} style={{ padding: 12, background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{kpi.kpiName}</div>
                  <div style={{ fontSize: 14, color: '#374151' }}>{getHealthIcon(kpi.currentHealth)} Current: {kpi.currentValue} {kpi.unit}</div>
                  <div className="text-text-dark-secondary" style={{ fontSize: 13 }}>Target: {kpi.targetOperator} {kpi.targetValue} {kpi.unit}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {initiative.milestones?.length > 0 && (
        <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ marginBottom: 24 }}>
          <div className="p-5">
            <h3 className="text-[15px] font-semibold text-text-dark" style={{ marginTop: 0 }}>Milestones</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {initiative.milestones.map(m => (
                <li key={m.id} style={{ padding: '8px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{m.completed ? <CheckCircle size={16} color="#2EB87A" strokeWidth={2} /> : <Circle size={16} color="#9CA3AF" strokeWidth={2} />}</span>
                  <span>{m.name}</span>
                  <span className="text-text-dark-secondary" style={{ fontSize: 14 }}>Due: {formatDate(m.dueDate)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {initiative.statusUpdates?.length > 0 && (
        <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ marginBottom: 24 }}>
          <div className="p-5">
            <h3 className="text-[15px] font-semibold text-text-dark" style={{ marginTop: 0 }}>Status Updates</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {initiative.statusUpdates.map(u => (
                <div key={u.id} style={{ padding: 16, background: '#F9FAFB', borderRadius: 8, borderLeft: '4px solid #2C5FA8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 8 }}>
                    <strong>{formatDate(u.updateDate)} — {u.authorName}</strong>
                    <span>{u.status} • {u.percentComplete != null ? `${u.percentComplete}%` : ''}</span>
                  </div>
                  <p style={{ margin: '0 0 8px 0', fontSize: 14 }}>{u.summary}</p>
                  {u.details && <p className="text-text-dark-secondary" style={{ margin: 0, fontSize: 13 }}>{u.details}</p>}
                  {u.accomplishments?.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      <strong>Accomplishments:</strong>
                      <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{u.accomplishments.map((a, i) => <li key={i}>{a}</li>)}</ul>
                    </div>
                  )}
                  {u.nextSteps?.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      <strong>Next steps:</strong>
                      <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{u.nextSteps.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {initiative.team?.length > 0 && (
        <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ marginBottom: 24 }}>
          <div className="p-5">
            <h3 className="text-[15px] font-semibold text-text-dark" style={{ marginTop: 0 }}>Team</h3>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {initiative.team.map(m => (
                <div key={m.id} style={{ padding: 12, background: '#F9FAFB', borderRadius: 8, minWidth: 160 }}>
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  <div className="text-text-dark-secondary" style={{ fontSize: 13 }}>{m.role || 'Member'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
